import { isEmpty } from "lodash";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { RoundTripTypeEnum } from "@/constants";
import { FLIGHT_PAGE_CHANGE_FILTER, TOOLS_AIRLINE_FINDER } from "@/constants/eventName";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { useListenEvent } from "@/hooks/useEventEmitter";
import usePathname from "@/hooks/usePathname";

import { FilterContextProvider } from "./FilterContext";
import FilterList from "./components/FilterList";
import { getFilterDefault } from "./filters";
import FilterItemDatesV2 from "./filters/DatesV2";
import { useFilterArriveAirportCabins } from "./hooks/useFilterArriveAirportCabins";
import { useFilterCabinPrice } from "./hooks/useFilterCabinPrice";
import { useFilterCabins } from "./hooks/useFilterCabins";
import { useFilterCardCabins } from "./hooks/useFilterCardCabins";
import useFilterCondition from "./hooks/useFilterCondition";
import { useFilterConnectionAirportCabins } from "./hooks/useFilterConnectionAirportCabins";
import { useFilterDateCabins } from "./hooks/useFilterDateCabins";
import { useFilterDatePrice } from "./hooks/useFilterDatePrice";
import { useFilterDaysCabins } from "./hooks/useFilterDaysCabins";
import { useFilterDepartAirportCabins } from "./hooks/useFilterDepartAirportCabins";
import { useFilterList } from "./hooks/useFilterList";
import { useFilterOptions } from "./hooks/useFilterOptions";
import { useFilterOptionsCount } from "./hooks/useFilterOptionsCount";
import { useFilterProgramsCabins } from "./hooks/useFilterProgramsCabins";
import { useFilterStopsCabins } from "./hooks/useFilterStopsCabins";
import { FilterData, FilterFieldKey, FilterSourceData } from "./typing";

interface FilterBarProps {
  dataSourceList: FilterSourceData;
  onChange: (result: FilterSourceData, filter?: FilterData) => void;
  roundTripType: RoundTripTypeEnum;
  initValues?: Partial<FilterData> | null;

  listViewType: number;

  loading?: boolean;

  useWhere?: string;
}

const FilterBar = (props: FilterBarProps) => {
  const { onChange, listViewType, loading = false, useWhere = "realTime" } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { isDealsFlight } = usePathname();

  const [open, setOpen] = useState(false);
  const [fieldId, setFieldId] = useState<FilterFieldKey>("all");

  const defaultFilterData = useRef<FilterData>(getFilterDefault());
  const [filterData, setFilterData] = useState<FilterData>(() => {
    return {
      ...defaultFilterData.current,
      ...props.initValues,
    };
  });

  const filterOptions = useFilterOptions(props.dataSourceList, props.roundTripType);

  const data = useFilterList(props.dataSourceList, filterData, listViewType);

  const condition = useFilterCondition(filterOptions, listViewType);

  const filterOptionsCount = useFilterOptionsCount(props.dataSourceList, data);

  const { user_preference } = useGlobalStoreContext();

  const filterDataExcludeDate = useMemo(() => ({ ...filterData, date: [] }), [filterData]);
  const dataExcludeDate = useFilterList(props.dataSourceList, filterDataExcludeDate, listViewType);

  const filterDataExcludePrograms = useMemo(() => ({ ...filterData, programs: [] }), [filterData]);
  const dataExcludePrograms = useFilterList(
    props.dataSourceList,
    filterDataExcludePrograms,
    listViewType
  );

  const filterDataExcludeCabins = useMemo(() => ({ ...filterData, cabins: [] }), [filterData]);
  const dataExcludeCabins = useFilterList(
    props.dataSourceList,
    filterDataExcludeCabins,
    listViewType
  );

  const filterDataExcludeBanks = useMemo(() => ({ ...filterData, banks: [] }), [filterData]);
  const dataExcludeBanks = useFilterList(
    props.dataSourceList,
    filterDataExcludeBanks,
    listViewType
  );

  const filterDataExcludeStops = useMemo(() => ({ ...filterData, stops: "-1" }), [filterData]);
  const dataExcludeStops = useFilterList(
    props.dataSourceList,
    filterDataExcludeStops,
    listViewType
  );

  const filterDataExcludeGridModeCabins = useMemo(
    () => ({ ...filterData, gridModeCabins: [] }),
    [filterData]
  );
  const dataExcludeGridModeCabins = useFilterList(
    props.dataSourceList,
    filterDataExcludeGridModeCabins,
    listViewType
  );

  const filterDataExcludeDepartAirport = useMemo(
    () => ({ ...filterData, departAirport: [] }),
    [filterData]
  );
  const dataExcludeDepartAirport = useFilterList(
    props.dataSourceList,
    filterDataExcludeDepartAirport,
    listViewType
  );

  const filterDataExcludeArriveAirport = useMemo(
    () => ({ ...filterData, arriveAirport: [] }),
    [filterData]
  );
  const dataExcludeArriveAirport = useFilterList(
    props.dataSourceList,
    filterDataExcludeArriveAirport,
    listViewType
  );

  const filterDataExcludeConnectingAirports = useMemo(
    () => ({ ...filterData, connectingAirports: [] }),
    [filterData]
  );
  const dataExcludeConnectingAirports = useFilterList(
    props.dataSourceList,
    filterDataExcludeConnectingAirports,
    listViewType
  );

  const filterDataExcludeDays = useMemo(() => ({ ...filterData, days: [] }), [filterData]);
  const dataExcludeDays = useFilterList(props.dataSourceList, filterDataExcludeDays, listViewType);

  const filterProgramsCabins = useFilterProgramsCabins(dataExcludePrograms);
  const filterCardsCabins = useFilterCardCabins(dataExcludeBanks);
  const filterStopsCabins = useFilterStopsCabins(dataExcludeStops);
  const filterCabinsCabins = useFilterCabins(dataExcludeGridModeCabins);
  const filterDateCabins = useFilterDateCabins(dataExcludeDate);

  const datePriceMap = useFilterDatePrice(dataExcludeDate);
  const datePriceMapSource = useFilterDatePrice(props.dataSourceList);
  const cabinPriceMap = useFilterCabinPrice(dataExcludeCabins);

  const filterDepartAirportCabins = useFilterDepartAirportCabins(dataExcludeDepartAirport);
  const filterArriveAirportCabins = useFilterArriveAirportCabins(dataExcludeArriveAirport);
  const filterConnectingAirportsCabins = useFilterConnectionAirportCabins(
    dataExcludeConnectingAirports
  );

  const filterDaysCabins = useFilterDaysCabins(dataExcludeDays);

  const _setFilterData: React.Dispatch<React.SetStateAction<FilterData>> = useCallback((v) => {
    // console.log("%c [  ]-56", "font-size:13px; background:pink; color:#bf2c9f;", v);
    setFilterData(v);
  }, []);

  // 监听全局变量，自动设置用户偏好过滤项目
  useEffect(() => {
    // 如果有alert带过来的filter，alert的筛选项优先级更高
    if (isEmpty(props.initValues)) {
      const data = {};
      if (user_preference?.cards?.length > 0) {
        Object.assign(data, { banks: user_preference.cards });
      }
      if (user_preference?.programs?.length > 0) {
        Object.assign(data, { programs: user_preference.programs });
      }
      // FIXME: 跟据条件显示是否过滤
      if (user_preference?.percent_premium > 0 && condition.premium) {
        Object.assign(data, { premium: [user_preference.percent_premium, 100] });
      }
      _setFilterData((state) => ({
        ...state,
        ...data,
      }));
    }
  }, [user_preference, props.initValues, condition.premium, _setFilterData]);

  useEffect(() => {
    onChange && onChange(data, filterData);
  }, [data, onChange, filterData]);

  const resetFilterField = useEvent((key: keyof FilterData) => {
    if (key === "airlines") {
      if (key === "airlines") {
        setFilterData({
          ...filterData,
          [key]: defaultFilterData.current[key],
          excludeAirlines: [],
        });
        return;
      }
    }
    _setFilterData({
      ...filterData,
      [key]: defaultFilterData.current[key],
    });
  });

  const resetFilterData = useEvent(() => {
    _setFilterData({
      ...defaultFilterData.current,
    });
  });

  const resetPartialFilterData = useEvent(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { programs, airlines, ...restFilter } = defaultFilterData.current;
    _setFilterData({
      ...filterData,
      ...restFilter,
    });
  });

  const handleChangeFilter = useCallback(
    (value: Partial<FilterData>) => {
      _setFilterData({
        ...filterData,
        ...value,
      });
    },
    [filterData, _setFilterData]
  );

  // useUpdateEffect(() => {
  //   resetPartialFilterData();
  // }, [props.roundTripType]);

  useListenEvent(TOOLS_AIRLINE_FINDER, (v: FilterData) => {
    if (isDealsFlight) {
      setFilterData({
        ...filterData,
        ...v,
      });
    }
  });
  useListenEvent(FLIGHT_PAGE_CHANGE_FILTER, (v: FilterData) => {
    setFilterData({
      ...filterData,
      ...v,
    });
  });

  useEffect(() => {
    // 重新搜索时，重置日期筛选
    if (loading) {
      setFilterData((prev) => {
        return {
          ...prev,
          date: [],
        };
      });
    }
  }, [loading]);

  return (
    <FilterContextProvider
      value={{
        filterOptions,
        filterOptionsCount,
        filterData,
        condition,
        defaultFilterData: defaultFilterData.current,
        setFilterData: handleChangeFilter,
        anchorEl,
        setAnchorEl,
        open,
        setOpen,
        fieldId,
        setFieldId,
        useWhere,

        resetFilterField,
        resetFilterData,

        datePriceMap,
        datePriceMapSource,
        cabinPriceMap,
        loading,

        filterProgramsCabins,
        filterCardsCabins,
        filterStopsCabins,
        filterCabinsCabins,
        filterDateCabins,
        filterDepartAirportCabins,
        filterArriveAirportCabins,
        filterConnectingAirportsCabins,
        filterDaysCabins,
      }}
    >
      <FilterList />

      <FilterItemDatesV2 />
    </FilterContextProvider>
  );
};

export default React.memo(FilterBar);
